import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Eye,
  MousePointer,
  TrendingUp,
  DollarSign,
  Heart,
  MessageCircle,
  Share2,
  UserPlus,
  Facebook,
  Instagram,
  Users,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const platformColors = {
  facebook: "#1877f2",
  instagram: "#e4405f",
  tiktok: "#000000",
};

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const MetricCard = ({ title, value, icon: Icon, color, subValue, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 card-hover metric-card"
    data-testid={`metric-card-${title.toLowerCase().replace(/\s/g, '-')}`}
  >
    <div className="flex items-start justify-between mb-4">
      <div
        className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}
      >
        <Icon className="w-6 h-6 text-white" />
      </div>
      {subValue && (
        <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
          {subValue}
        </span>
      )}
    </div>
    <h3 className="text-2xl font-bold text-slate-900 font-['Outfit']">
      {typeof value === "number" ? value.toLocaleString() : value}
    </h3>
    <p className="text-sm text-slate-500 mt-1">{title}</p>
  </motion.div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="glass rounded-lg p-3 shadow-lg">
        <p className="text-sm font-medium text-slate-900">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value.toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Dashboard({ clients, metrics, adMetrics, onClientSelect, loading }) {
  const [stats, setStats] = useState(null);
  const [platformStats, setPlatformStats] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [overviewRes, platformRes] = await Promise.all([
          axios.get(`${API}/stats/overview`),
          axios.get(`${API}/stats/by-platform`),
        ]);
        setStats(overviewRes.data);
        setPlatformStats(platformRes.data);
      } catch (e) {
        console.error("Error fetching stats:", e);
      }
    };
    fetchStats();
  }, [metrics]);

  // Prepare chart data
  const chartData = metrics
    .slice(0, 30)
    .reverse()
    .map((m) => ({
      date: m.date,
      impressions: m.impressions,
      clicks: m.clicks,
      engagement: m.likes + m.comments + m.shares,
    }));

  const pieData = platformStats.map((p) => ({
    name: p.platform.charAt(0).toUpperCase() + p.platform.slice(1),
    value: p.impressions,
    color: platformColors[p.platform] || "#7c3aed",
  }));

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="h-8 w-64 skeleton rounded-lg" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-32 skeleton rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-['Outfit']">
          Dashboard Overview
        </h1>
        <p className="text-slate-500 mt-1">
          Track your social media performance across all clients
        </p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Impressions"
          value={stats?.total_impressions || 0}
          icon={Eye}
          color="bg-violet-500"
          delay={0.1}
        />
        <MetricCard
          title="Total Clicks"
          value={stats?.total_clicks || 0}
          icon={MousePointer}
          color="bg-pink-500"
          delay={0.15}
        />
        <MetricCard
          title="Average CTR"
          value={`${stats?.avg_ctr || 0}%`}
          icon={TrendingUp}
          color="bg-sky-500"
          delay={0.2}
        />
        <MetricCard
          title="Total Spend"
          value={`$${stats?.total_spend?.toLocaleString() || 0}`}
          icon={DollarSign}
          color="bg-emerald-500"
          delay={0.25}
        />
        <MetricCard
          title="Total Likes"
          value={stats?.total_likes || 0}
          icon={Heart}
          color="bg-red-500"
          delay={0.3}
        />
        <MetricCard
          title="Total Comments"
          value={stats?.total_comments || 0}
          icon={MessageCircle}
          color="bg-amber-500"
          delay={0.35}
        />
        <MetricCard
          title="Total Shares"
          value={stats?.total_shares || 0}
          icon={Share2}
          color="bg-indigo-500"
          delay={0.4}
        />
        <MetricCard
          title="New Followers"
          value={stats?.total_new_followers || 0}
          icon={UserPlus}
          color="bg-teal-500"
          delay={0.45}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Area Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="lg:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-slate-100"
          data-testid="performance-chart"
        >
          <h3 className="text-lg font-semibold text-slate-900 font-['Outfit'] mb-4">
            Performance Trends
          </h3>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorImpressions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#db2777" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#db2777" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="impressions"
                  stroke="#7c3aed"
                  fillOpacity={1}
                  fill="url(#colorImpressions)"
                  name="Impressions"
                />
                <Area
                  type="monotone"
                  dataKey="clicks"
                  stroke="#db2777"
                  fillOpacity={1}
                  fill="url(#colorClicks)"
                  name="Clicks"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-slate-400">
              No data available. Add metrics to see trends.
            </div>
          )}
        </motion.div>

        {/* Platform Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-slate-100"
          data-testid="platform-distribution"
        >
          <h3 className="text-lg font-semibold text-slate-900 font-['Outfit'] mb-4">
            Platform Distribution
          </h3>
          {pieData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-3 mt-4 justify-center">
                {pieData.map((p, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: p.color }}
                    />
                    <span className="text-sm text-slate-600">{p.name}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-slate-400">
              No platform data
            </div>
          )}
        </motion.div>
      </div>

      {/* Clients Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white rounded-xl p-6 shadow-sm border border-slate-100"
        data-testid="clients-overview"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-slate-900 font-['Outfit']">
            Active Clients
          </h3>
          <span className="text-sm text-slate-500">
            {clients.length} client{clients.length !== 1 ? "s" : ""}
          </span>
        </div>
        {clients.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clients.slice(0, 6).map((client) => (
              <motion.button
                key={client.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onClientSelect(client)}
                className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                data-testid={`client-card-${client.id}`}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white font-bold text-lg">
                  {client.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-slate-900 truncate">
                    {client.name}
                  </h4>
                  <div className="flex gap-2 mt-1">
                    {client.platforms.includes("facebook") && (
                      <Facebook className="w-4 h-4 text-blue-600" />
                    )}
                    {client.platforms.includes("instagram") && (
                      <Instagram className="w-4 h-4 text-pink-600" />
                    )}
                    {client.platforms.includes("tiktok") && (
                      <span className="text-black">
                        <TikTokIcon />
                      </span>
                    )}
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-slate-400">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No clients yet. Add your first client to get started.</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
